import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useShiftCodes, useCreateTemplate, useUpdateTemplate } from '@/hooks/schedule';
import { useAuth } from '@/hooks/useAuth';
import type { ShiftTemplate, TemplateShift, TemplateWeek } from '@/services/schedule/templateService';

interface TemplateEditorProps {
  open: boolean;
  template: ShiftTemplate | null;
  onClose: () => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

export const TemplateEditor = ({ open, template, onClose }: TemplateEditorProps) => {
  const { profile } = useAuth();
  const { data: shiftCodes } = useShiftCodes(profile?.company_id);
  const { mutate: createTemplate, isPending: isCreating } = useCreateTemplate();
  const { mutate: updateTemplate, isPending: isUpdating } = useUpdateTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [numWeeks, setNumWeeks] = useState(1);
  const [weekShifts, setWeekShifts] = useState<Map<number, Map<number, string>>>(new Map());

  const isEditing = !!template?.id;
  const isSaving = isCreating || isUpdating;

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setNumWeeks(template.num_weeks);

      // Build week shifts map from template data
      const newWeekShifts = new Map<number, Map<number, string>>();
      template.weeks?.forEach((week) => {
        const dayShifts = new Map<number, string>();
        week.shifts?.forEach((shift) => {
          dayShifts.set(shift.day_of_week, shift.shift_code);
        });
        newWeekShifts.set(week.week_number, dayShifts);
      });
      setWeekShifts(newWeekShifts);
    } else {
      // Reset for new template
      setName('');
      setDescription('');
      setNumWeeks(1);
      setWeekShifts(new Map());
    }
  }, [template, open]);

  const handleShiftCodeChange = (weekNumber: number, dayOfWeek: number, shiftCode: string | null) => {
    const newWeekShifts = new Map(weekShifts);
    if (!newWeekShifts.has(weekNumber)) {
      newWeekShifts.set(weekNumber, new Map());
    }
    
    const dayShifts = newWeekShifts.get(weekNumber)!;
    if (shiftCode === null || shiftCode === '') {
      dayShifts.delete(dayOfWeek);
    } else {
      dayShifts.set(dayOfWeek, shiftCode);
    }
    
    setWeekShifts(newWeekShifts);
  };

  const handleSave = () => {
    // Build weeks array for submission
    const weeks: TemplateWeek[] = [];
    for (let weekNum = 1; weekNum <= numWeeks; weekNum++) {
      const dayShifts = weekShifts.get(weekNum) || new Map();
      const shifts: TemplateShift[] = [];
      
      dayShifts.forEach((shiftCode, dayOfWeek) => {
        shifts.push({
          day_of_week: dayOfWeek,
          shift_code: shiftCode,
        });
      });

      weeks.push({
        week_number: weekNum,
        shifts,
      });
    }

    const input = {
      name,
      description: description || undefined,
      num_weeks: numWeeks,
      weeks,
    };

    if (isEditing) {
      updateTemplate(
        { id: template.id, updates: input },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    } else {
      createTemplate(input, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  const canSave = name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Template' : 'Create New Template'}</DialogTitle>
          <DialogDescription>
            Define a shift pattern that can be reused across multiple employees
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Standard Week, 2-Week Rotation"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">Description (Optional)</Label>
              <Textarea
                id="template-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe when to use this template"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="num-weeks">Number of Weeks</Label>
              <Select
                value={numWeeks.toString()}
                onValueChange={(value) => setNumWeeks(parseInt(value))}
              >
                <SelectTrigger id="num-weeks" className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Week</SelectItem>
                  <SelectItem value="2">2 Weeks</SelectItem>
                  <SelectItem value="3">3 Weeks</SelectItem>
                  <SelectItem value="4">4 Weeks</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Pattern will repeat when applied to a full month
              </p>
            </div>
          </div>

          {/* Week Grids */}
          <div className="space-y-6">
            {Array.from({ length: numWeeks }, (_, i) => i + 1).map((weekNum) => (
              <div key={weekNum} className="space-y-3">
                <h4 className="font-medium">Week {weekNum}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {DAYS_OF_WEEK.map((day) => (
                    <div key={day.value} className="space-y-1.5">
                      <Label className="text-xs">{day.label}</Label>
                      <Select
                        value={weekShifts.get(weekNum)?.get(day.value) || 'none'}
                        onValueChange={(value) =>
                          handleShiftCodeChange(weekNum, day.value, value === 'none' ? null : value)
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="No shift" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No shift</SelectItem>
                          {shiftCodes?.map((code) => (
                            <SelectItem key={code.code} value={code.code}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-sm"
                                  style={{ backgroundColor: code.color }}
                                />
                                <span>{code.code}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave || isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
